import { Box, Checkbox, Chip, List, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ReactNode } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { IManagedUserAccessDataModel } from '../../../data-models/managed-user-access.data-model';
import { AddRemoveListItem } from './AddRemoveListItem';
import { CheckboxListItem } from './CheckboxListItem';
import { LeftPanel, RightPanel } from './ManageAccessPanels';
import { compareByAccessState } from './useManageAccess.view-model';

type CompanyItem = Pick<ICompanyDataModel, 'id' | 'name' | 'logoUrl'>;

interface CompanyViewProps {
  companies: Array<CompanyItem>;
  filteredCompanies: Array<CompanyItem>;
  companyFilter: string;
  onCompanyFilterChange: (filter: string) => void;
  getAggregatedAccessStateForUser: (email: string) => 'all' | 'partial' | 'none';
  getAccessForCompany: (companyId: number) => IManagedUserAccessDataModel[];
  onBulkAdd: (identifier: string | number) => void;
  onBulkRemove: (identifier: string | number) => void;
  onCompanyCheckboxToggle: (companyId: number) => void;
  onCompanySelection: (companyId: number) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  selectedCompanies: number[];
  userList: Array<{ id: number; email: string }>;
}

export const CompanyView = ({
  companies,
  filteredCompanies,
  companyFilter,
  onCompanyFilterChange,
  getAggregatedAccessStateForUser,
  getAccessForCompany,
  onBulkAdd,
  onBulkRemove,
  onCompanyCheckboxToggle,
  onCompanySelection,
  onSelectAll,
  onDeselectAll,
  selectedCompanies,
  userList,
}: CompanyViewProps) => {
  const companyName =
    selectedCompanies.length === 1 ? companies.find((c) => c.id === selectedCompanies[0])?.name : '';

  function getTopMessage(count: number, companyName?: string) {
    if (count === 0) {
      return 'Select one or more companies to manage assignee(s)';
    } else if (count === 1) {
      return companyName ? `Update assignees to ${companyName}` : 'Company not found';
    } else {
      return (
        <span>
          Update assignees to selected <b>{`${count} companies`}</b>
        </span>
      );
    }
  }

  function getSecondaryLabel(assignedCount: number): string {
    if (assignedCount === 0) {
      return 'No assignees';
    } else if (assignedCount === 1) {
      return '1 assignee';
    }
    return `${assignedCount} assignees`;
  }

  function getChip(selectedCount: number, accessState: string): ReactNode {
    if (selectedCount === 1) {
      return accessState === 'all' ? (
        <Chip label='Has Access' color='success' size='small' variant='outlined' />
      ) : (
        <Chip label='No Access' color='default' size='small' variant='outlined' />
      );
    }
    if (accessState === 'all') {
      return <Chip label='Has Access to All Selected' color='success' size='small' variant='outlined' />;
    } else if (accessState === 'partial') {
      return <Chip label='Has Access to Some Selected' color='warning' size='small' variant='outlined' />;
    } else {
      return <Chip label='No Access' color='default' size='small' variant='outlined' />;
    }
  }

  const allSelected = filteredCompanies.length > 0 && selectedCompanies.length === filteredCompanies.length;
  const someSelected = selectedCompanies.length > 0 && selectedCompanies.length < filteredCompanies.length;

  const sortedUsers = userList
    .map((user) => {
      return {
        ...user,
        accessState: getAggregatedAccessStateForUser(user.email),
      };
    })
    .sort(compareByAccessState);

  return (
    <Grid container spacing={4} sx={{ height: '100%' }} flex={1}>
      <LeftPanel
        header={
          <Box>
            <TextField
              fullWidth
              placeholder='Filter companies...'
              onChange={(e) => onCompanyFilterChange(e.target.value)}
              value={companyFilter}
            />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={allSelected ? onDeselectAll : onSelectAll}
              />
              <Typography variant='subtitle2'>Select All Companies</Typography>
            </Box>
          </Box>
        }
        list={
          <Virtuoso
            data={filteredCompanies}
            itemContent={(_, company) => {
              return (
                <CheckboxListItem
                  key={company.id}
                  avatarUrl={company.logoUrl ?? ''}
                  checked={selectedCompanies.includes(company.id)}
                  data={company.id}
                  id={company.id}
                  onCardClick={onCompanySelection}
                  onCheckboxChange={onCompanyCheckboxToggle}
                  primary={company.name}
                  secondary={`${getSecondaryLabel(getAccessForCompany(company.id).length)}`}
                  selected={selectedCompanies.includes(company.id)}
                />
              );
            }}
          />
        }
      />

      <RightPanel
        selectedItemCount={selectedCompanies.length}
        topMessage={getTopMessage(selectedCompanies.length, companyName)}
      >
        <List sx={{ width: '100%' }}>
          {sortedUsers.map((user) => {
            return (
              <AddRemoveListItem
                key={user.id}
                primary={user.email}
                secondary={getChip(selectedCompanies.length, user.accessState)}
                accessState={user.accessState}
                onAdd={() => onBulkAdd(user.email)}
                onRemove={() => onBulkRemove(user.email)}
                addTitle='Add access to all selected companies'
                removeTitle='Remove access from all selected companies'
              />
            );
          })}
        </List>
      </RightPanel>
    </Grid>
  );
};
